import React, { createContext, useCallback, useState, useContext } from 'react';
import api from '../services/api';

import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

export interface IMedicamento {
  MedCodigo: number;
  MedRecCodigo: number;
  MedNome: string;
  MedQuantidade: string;
  MedObservacao?: string;
  MedControlado: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface IReceita {
  RecCodigo: number;
  RecControlado: boolean;
  RecPacCodigo: number;
  RecUsuCodigo: number;
  RecCliCodigo: number;
  RecPacNome: string;
  RecPacDataNasc: Date;
  RecPacTelefone?: string;
  RecPacCPF?: string;
  RecPacEndereco?: string;
  RecUsuNome: string;
  RecUsuCRM?: string;
  RecUsuEndereco?: string;
  RecInformacao?: string;
  RecStatus: 'Criada' | 'Emitida' | 'Cancelada';
  RecArquivo?: string;
  arquivo_url?: string;
  created_at: Date;
  updated_at: Date;
  medicamentos: IMedicamento[];
}

export interface IReceitaSearch {
  RecCodigo: number;
  RecControlado: boolean;
  RecPacCodigo: number;
  RecPacNome: string;
  RecPacCPF: string;
  RecPacCPFFormatado: string;
  RecUsuCodigo: number;
  RecUsuNome: string;
  RecUsuCRM: string;
  RecStatus: string;
  RecInformacao: string | null;
  RecArquivo: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface IReturnSearchReceita {
  data: IReceitaSearch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ICreateMedicamentoDTO {
  MedNome: string;
  MedQuantidade: string;
  MedObservacao?: string;
  MedControlado: boolean;
}

export interface ICreateReceitaDTO {
  RecPacCodigo: number;
  RecUsuCodigo: number;
  RecPacNome: string;
  RecPacTelefone?: string;
  RecPacCPF?: string;
  RecPacEndereco?: string;
  RecUsuNome: string;
  RecUsuCRM?: string;
  RecUsuEndereco?: string;
  RecInformacao?: string;
  RecArquivo?: string;
  medicamentos: ICreateMedicamentoDTO[];
}

export interface IUpdateReceitaDTO {
  RecControlado?: boolean;
  RecPacNome?: string;
  RecPacTelefone?: string;
  RecPacCPF?: string;
  RecPacEndereco?: string;
  RecUsuNome?: string;
  RecUsuCRM?: string;
  RecUsuEndereco?: string;
  RecInformacao?: string;
  RecArquivo?: string;
}

interface ReceitaContextData {
  loading: boolean;
  criarReceita(dados: ICreateReceitaDTO): Promise<IReceita>;
  buscarReceita(codigo: number): Promise<IReceita>;
  listarReceitasPorPaciente(pacCodigo: number): Promise<IReceita[]>;
  listarReceitas(filtros?: IFiltrosReceita): Promise<IReturnSearchReceita>;
  atualizarReceita(codigo: number, dados: IUpdateReceitaDTO): Promise<IReceita>;
  alterarStatus(
    codigo: number,
    status: 'Criada' | 'Emitida' | 'Cancelada',
  ): Promise<IReceita>;
  adicionarMedicamento(
    receitaCodigo: number,
    dados: ICreateMedicamentoDTO,
  ): Promise<IMedicamento>;
  removerMedicamento(
    receitaCodigo: number,
    medicamentoCodigo: number,
  ): Promise<void>;
  atualizarMedicamento(
    receitaCodigo: number,
    medicamentoCodigo: number,
    dados: ICreateMedicamentoDTO,
  ): Promise<IMedicamento>;
  assinarReceita(receitaCodigo: number): Promise<IReceita>;
  cancelarReceita(receitaCodigo: number): Promise<IReceita>;
  loginPrescreve(email: string, senha: string): Promise<void>;
  conectarCertificado(senha: string): Promise<void>;
}

export interface IFiltrosReceita {
  paciente?: string;
  medico?: string;
  status?: 'Criada' | 'Emitida' | 'Cancelada' | '';
  dataInicio?: string;
  dataFim?: string;
  pagina?: number;
  limite?: number;
}

const ReceitaContext = createContext<ReceitaContextData>(
  {} as ReceitaContextData,
);

const ReceitaProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const criarReceita = useCallback(
    async (dados: ICreateReceitaDTO): Promise<IReceita> => {
      setLoading(true);
      try {
        const response = await api.post<IReceita[]>('/receitas', dados);
        const mensagem =
          response.data.length === 1
            ? 'Receita criada com sucesso'
            : 'Receitas SIMPLES e ESPECIAL criadas';

        addToast({
          type: 'success',
          title: mensagem,
        });
        return response.data[0];
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao criar receita',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const buscarReceita = useCallback(
    async (codigo: number): Promise<IReceita> => {
      setLoading(true);
      try {
        const response = await api.get<IReceita>(`/receitas/${codigo}`);
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao buscar receita',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const listarReceitasPorPaciente = useCallback(
    async (pacCodigo: number): Promise<IReceita[]> => {
      setLoading(true);
      try {
        const response = await api.get<IReceita[]>(
          `/receitas/paciente/${pacCodigo}`,
        );
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao listar receitas do paciente',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const listarReceitas = useCallback(
    async (filtros?: IFiltrosReceita): Promise<IReturnSearchReceita> => {
      setLoading(true);
      try {
        let url = '/receitas/busca?';
        const params = new URLSearchParams();

        if (filtros?.paciente) params.append('paciente', filtros.paciente);
        if (filtros?.medico) params.append('medico', filtros.medico);
        if (filtros?.status) params.append('status', filtros.status);
        if (filtros?.dataInicio)
          params.append('dataInicio', filtros.dataInicio);
        if (filtros?.dataFim) params.append('dataFim', filtros.dataFim);
        if (filtros?.pagina) params.append('pagina', filtros.pagina.toString());
        if (filtros?.limite) params.append('limite', filtros.limite.toString());

        url += params.toString();
        const response = await api.get<IReturnSearchReceita>(url);
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao listar receitas',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const atualizarReceita = useCallback(
    async (codigo: number, dados: IUpdateReceitaDTO): Promise<IReceita> => {
      setLoading(true);
      try {
        const response = await api.put<IReceita>(`/receitas/${codigo}`, dados);
        addToast({
          type: 'success',
          title: 'Receita atualizada com sucesso',
        });
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao atualizar receita',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const alterarStatus = useCallback(
    async (
      codigo: number,
      status: 'Criada' | 'Emitida' | 'Cancelada',
    ): Promise<IReceita> => {
      setLoading(true);
      try {
        const response = await api.patch<IReceita>(
          `/receitas/${codigo}/status`,
          { RecStatus: status },
        );
        addToast({
          type: 'success',
          title: `Receita ${status.toLowerCase()} com sucesso`,
        });
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: `Erro ao ${status.toLowerCase()} receita`,
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const adicionarMedicamento = useCallback(
    async (
      receitaCodigo: number,
      dados: ICreateMedicamentoDTO,
    ): Promise<IMedicamento> => {
      setLoading(true);
      try {
        const response = await api.post<IMedicamento>(
          `/receitas/${receitaCodigo}/medicamentos`,
          dados,
        );
        addToast({
          type: 'success',
          title: 'Medicamento adicionado com sucesso',
        });
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao adicionar medicamento',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const removerMedicamento = useCallback(
    async (receitaCodigo: number, medicamentoCodigo: number): Promise<void> => {
      setLoading(true);
      try {
        await api.delete(
          `/receitas/${receitaCodigo}/medicamentos/${medicamentoCodigo}`,
        );
        addToast({
          type: 'success',
          title: 'Medicamento removido com sucesso',
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao remover medicamento',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const atualizarMedicamento = useCallback(
    async (
      receitaCodigo: number,
      medicamentoCodigo: number,
      dados: ICreateMedicamentoDTO,
    ): Promise<IMedicamento> => {
      setLoading(true);
      try {
        const response = await api.put<IMedicamento>(
          `/receitas/medicamentos/${medicamentoCodigo}`,
          dados,
        );
        addToast({
          type: 'success',
          title: 'Medicamento atualizado com sucesso',
        });
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao atualizar medicamento',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const assinarReceita = useCallback(
    async (receitaCodigo: number): Promise<IReceita> => {
      setLoading(true);
      try {
        const response = await api.patch<IReceita>(
          `/receitas/assinar/${receitaCodigo}`,
        );
        addToast({
          type: 'success',
          title: 'Recceita assinada com sucesso',
        });
        return response.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao assinar receita',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const cancelarReceita = useCallback(
    async (receitaCodigo: number): Promise<IReceita> => {
      setLoading(true);
      try {
        const receitaCancelada = await api.delete<IReceita>(
          `/receitas/cancelar/${receitaCodigo}`,
        );
        addToast({
          type: 'success',
          title: 'Recceita cancelada com sucesso',
        });
        return receitaCancelada.data;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao cancelar receita',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const loginPrescreve = useCallback(
    async (email: string, senha: string): Promise<void> => {
      setLoading(true);
      try {
        await api.post(`/prescreve/auth/login`, {
          email,
          senha,
        });
        addToast({
          type: 'success',
          title: 'Login realizado com sucesso',
        });
        return;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao realizar login',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const conectarCertificado = useCallback(
    async (senha: string): Promise<void> => {
      setLoading(true);
      try {
        await api.post(`/prescreve/cert/conectar`, {
          senha,
        });
        addToast({
          type: 'success',
          title: 'Certificado conectado com sucesso',
        });
        return;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao conectar certificado',
          description: mensagemErro,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <ReceitaContext.Provider
      value={{
        loading,
        criarReceita,
        buscarReceita,
        listarReceitasPorPaciente,
        listarReceitas,
        atualizarReceita,
        alterarStatus,
        adicionarMedicamento,
        removerMedicamento,
        atualizarMedicamento,
        assinarReceita,
        loginPrescreve,
        conectarCertificado,
        cancelarReceita,
      }}
    >
      {children}
    </ReceitaContext.Provider>
  );
};

function useReceita(): ReceitaContextData {
  const context = useContext(ReceitaContext);

  if (!context) {
    throw new Error('useReceita deve ser usado com o ReceitaProvider');
  }

  return context;
}

export { ReceitaProvider, useReceita };
